import { type SearchRequest } from '@searchkit/api';
import { merge } from 'merge-anything';

import { createLocalDateRuntimeField, hitRateRequiredQueryFactory } from '~/search-domain/queries';
import { mergeQueries } from '~/search-domain/utils';

export function createQueryFactory(params: {
  activeSort: { field: string; order: 'asc' | 'desc' };
  hitRateBucket?: string;
  last10PersistentSortEnabled: boolean;
  resultAttributes: string[];
  searchRequest: SearchRequest;
  timezone?: string;
}) {
  const { activeSort, hitRateBucket, last10PersistentSortEnabled, resultAttributes, searchRequest, timezone } = params;

  const hitsPerPage = searchRequest.request.params?.hitsPerPage ?? 10;
  const page = searchRequest.request.params?.page ?? 0;
  const sort = { [activeSort.field]: activeSort.order };

  const bucketsNeeded = (page + 1) * hitsPerPage;

  const shouldUseLast10PersistentSort =
    last10PersistentSortEnabled && !activeSort.field.includes('sportsData.hitRates.last');
  const termsOrder = [
    { sort_value: activeSort.order },
    ...(shouldUseLast10PersistentSort ? [{ last10_sort_value: 'desc' as const }] : []),
  ];

  return merge(searchRequest, {
    body: {
      aggs: {
        stable_total: { cardinality: { field: 'betId' } },
        bets_by_id: {
          terms: {
            field: 'betId',
            size: bucketsNeeded + 100,
            order: termsOrder,
          },
          aggs: {
            best_odds: {
              top_hits: {
                size: 1,
                sort: [{ oddsDecimal: 'desc' }],
                _source: { includes: resultAttributes },
              },
            },
            sort_value: { max: { field: activeSort.field } },
            ...(shouldUseLast10PersistentSort
              ? { last10_sort_value: { max: { field: 'sportsData.hitRates.last10' } } }
              : {}),
          },
        },
      },
      query: mergeQueries(
        searchRequest.body.query ?? {},
        ...(hitRateBucket ? [hitRateRequiredQueryFactory({ hitRateBucket })] : []),
      ),
      ...(timezone && { runtime_mappings: createLocalDateRuntimeField(timezone) }),
      sort: [sort, ...(shouldUseLast10PersistentSort ? [{ 'sportsData.hitRates.last10': 'desc' as const }] : [])],
    },
  });
}
